import { useMemo } from "react";

import type { Quote } from "~/v2/api/rest/data";

interface UsePriceProps {
  quote: Quote | undefined;
}

export const getPrice = ({
  quote,
}: UsePriceProps): {
  midPrice: { value: number; display: string };
  priceChange: { value: number; percentage?: string };
} => {
  const last = quote?.last ?? 0;
  const lastDayClosing = Number.parseFloat(quote?.last_day_closing ?? "");
  const hasPreviousClose =
    quote?.last !== undefined &&
    Number.isFinite(lastDayClosing) &&
    lastDayClosing > 0;
  const priceChange = hasPreviousClose ? last - lastDayClosing : 0;
  const percentChange = hasPreviousClose
    ? (priceChange / lastDayClosing) * 100
    : undefined;

  const mid =
    quote?.ask !== undefined && quote?.bid !== undefined
      ? (quote.ask + quote.bid) / 2
      : undefined;

  return {
    midPrice: mid
      ? {
          value: mid,
          display: mid.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: "currency",
            currency: "USD",
          }),
        }
      : {
          value: 0,
          display: "-",
        },
    priceChange: {
      value: priceChange,
      percentage:
        percentChange !== undefined
          ? `${priceChange > 0 ? "+" : ""}${percentChange.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              },
            )}%`
          : undefined,
    },
  };
};

export const usePrice = ({ quote }: UsePriceProps) => {
  const { midPrice, priceChange } = useMemo(() => getPrice({ quote }), [quote]);
  return { midPrice, priceChange };
};
